import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ConfirmationModal from 'components/ConfirmationModal';
import { useState } from 'react';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';

type ExistingCustomerSearchSectionProps = {
  companyId: number;
  branchId: number;
  loading?: boolean;
  onAttachCustomer: (customerId: number | string) => Promise<void>;
  onError: (error: any) => void;
};

const ExistingCustomerSearchSection = ({
  companyId,
  branchId,
  loading = false,
  onAttachCustomer,
  onError,
}: ExistingCustomerSearchSectionProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const searchCustomers = async () => {
    if (!query.trim()) {
      setResults([]);
      setSearched(false);
      setResultsOpen(false);
      return;
    }

    try {
      setSearchLoading(true);
      setSearched(true);
      const { data } = await axiosInstance.get(URLS.searchCustomersWithMappings(query.trim()));
      setResults(data || []);
      setResultsOpen(true);
    } catch (error: any) {
      onError(error);
    } finally {
      setSearchLoading(false);
    }
  };

  const customerMappingText = (customer: any, alreadyMapped: boolean) => {
    if (alreadyMapped) return 'Already mapped to this branch';
    if ((customer.companyBranchMappings || []).length === 0) return 'No company branch mapping';

    return customer.companyBranchMappings
      .map((mapping: any) => `${mapping.salesCompany?.label || '-'} / ${mapping.salesBranch?.label || '-'}`)
      .join(', ');
  };

  const confirmAttachCustomer = async () => {
    if (!selectedCustomer?.id) return;
    await onAttachCustomer(selectedCustomer.id);
    setSelectedCustomer(null);
    setResultsOpen(false);
  };

  return (
    <Box
      sx={{
        mb: 2.5,
        px: 0,
        pb: 2,
        pt: 3,
        mt: 2,
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <PersonAddAltIcon color="primary" fontSize="small" />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Search Existing Contact
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.25, flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextField
          fullWidth
          size="small"
          label="Contact name, email or phone"
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setSearched(false);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              searchCustomers();
            }
          }}
        />
        <Button
          variant="contained"
          startIcon={!searchLoading ? <SearchIcon /> : undefined}
          onClick={searchCustomers}
          disabled={searchLoading}
          sx={{ height: 40, minWidth: 112 }}>
          {searchLoading ? <CircularProgress color="inherit" size={18} /> : 'Search'}
        </Button>
      </Box>

      <Dialog open={resultsOpen} onClose={() => setResultsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          Select Existing Contact
          <IconButton
            aria-label="close"
            onClick={() => setResultsOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 10 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 360, maxHeight: 520, pt: 1.5 }}>
          {results.length > 0 ? (
            <List disablePadding sx={{ display: 'grid', gap: 1.25 }}>
              {results.map(customer => {
                const alreadyMapped = customer.companyBranchMappings?.some(
                  (mapping: any) =>
                    Number(mapping.salesCompany?.value) === Number(companyId) &&
                    Number(mapping.salesBranch?.value) === Number(branchId),
                );
                return (
                  <Box key={customer.id}>
                    <ListItemButton
                      disabled={alreadyMapped || loading}
                      onClick={() => setSelectedCustomer(customer)}
                      sx={{
                        alignItems: 'flex-start',
                        px: 2,
                        py: 2,
                        border: theme => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      }}>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 700 }}>
                            {customer.fullName || [customer.firstName, customer.lastName].filter(Boolean).join(' ')}
                          </Typography>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'grid', gap: 0.75, mt: 0.75 }}>
                            {(customer.phone || customer.email) && (
                              <Box
                                component="span"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  columnGap: 1,
                                  rowGap: 0.5,
                                  flexWrap: 'wrap',
                                }}>
                                {customer.phone && (
                                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <PhoneOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography component="span" variant="body2" color="text.secondary">
                                      {customer.phone}
                                    </Typography>
                                  </Box>
                                )}
                                {customer.phone && customer.email && (
                                  <Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />
                                )}
                                {customer.email && (
                                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <EmailOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography component="span" variant="body2" color="text.secondary">
                                      {customer.email}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography component="span" variant="body2" color="text.secondary">
                                {customerMappingText(customer, alreadyMapped)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </Box>
                );
              })}
            </List>
          ) : (
            searched &&
            !searchLoading && (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 700 }}>No contacts found</Typography>
                <Typography variant="body2" color="text.secondary">
                  Try another name, email, or phone number.
                </Typography>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResultsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={!!selectedCustomer}
        title="Add Existing Contact"
        message={`Do you want to add ${selectedCustomer?.fullName || 'this contact'} as a customer for this branch?`}
        confirmText="Add Customer"
        cancelText="Cancel"
        onCancel={() => setSelectedCustomer(null)}
        onConfirm={confirmAttachCustomer}
      />
    </Box>
  );
};

export default ExistingCustomerSearchSection;
